// src/pages/Trend/TrendPage.js
import React, { useState, useCallback } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTrendState } from '../../hooks/useTrendState.js';
import { useBrandState } from '../../hooks/useBrandState.js';
import { LoadingSpinner, ErrorMessage } from '../../components/common/UI.js';
import { EmptyStateGuide } from '../../components/common/EmptyStateGuide.js';
import TrendTutorial from '../../components/common/TrendTutorial.js';
import FilterPanel from './components/filters/FilterPanel.js';
import BrandContentArea from './components/brand/BrandContentArea.js';
import DashboardContent from './components/dashboard/DashboardContent.js';
import { MarketTrendsTab } from './components/analysis/tabs/MarketTrendsTab.js';
import { ProductAnalysisTab } from './components/analysis/tabs/ProductAnalysisTab.js';
import ChartContainer from './components/analysis/common/ChartContainer.js';
// Consumer Reaction Charts
import Summary from './components/analysis/consumer-reaction/Summary.js';
import SentimentChart from './components/analysis/consumer-reaction/SentimentChart.js';
import AspectChart from './components/analysis/consumer-reaction/AspectChart.js';
import KeywordsChart from './components/analysis/consumer-reaction/KeywordsChart.js';
// Ingredient Analysis Charts
import RankingChart from './components/analysis/ingredient-analysis/RankingChart.js';
import ClassificationAnalysisChart from './components/analysis/ingredient-analysis/ClassificationAnalysisChart.js';
import CategoryAnalysisChart from './components/analysis/ingredient-analysis/CategoryAnalysisChart.js';
import OccurrenceChart from './components/analysis/ingredient-analysis/OccurrenceChart.js';
// Sustainability Charts
import SustainabilityStatusChart from './components/analysis/sustainability/SustainabilityStatusChart.js';
import CertificationChart from './components/analysis/sustainability/CertificationChart.js';
import './UnifiedDashboard.css';
import { TrendsProvider, useTrendsContext } from '../../contexts/TrendsContext.js';

const INITIAL_CATEGORY_ID = 'CM01';

/**
 * 트렌드 페이지의 주요 컨텐츠를 렌더링하는 컴포넌트
 * Context에서 필터 상태를 관리하고 필터 변경 시 차트를 업데이트
 */
const TrendsPageContent = () => {
  const { translate } = useLanguage();
  const { filters, updateFilters } = useTrendsContext();
  const [activePage, setActivePage] = useState(1); // 1 or 2
  const [activeTab, setActiveTab] = useState('dashboard'); // dashboard, brand, analysis
  const [hasVisitedAnalysis, setHasVisitedAnalysis] = useState(false);

  // Brand state management
  const brandState = useBrandState();

  // Track when Analysis tab is clicked for the first time (for tutorial)
  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
    if (tab === 'analysis' && !hasVisitedAnalysis) {
      setHasVisitedAnalysis(true);
    }
  }, [hasVisitedAnalysis]);

  /**
   * 필터 적용 시 호출되는 콜백 함수
   * @param {string} categoryId - 선택된 카테고리 ID
   * @param {Array<string>} mainIds - 주 필터 그룹 ID 목록
   * @param {Array<string>} comparisonIds - 비교 필터 그룹 ID 목록
   */
  const handleFiltersApply = useCallback((categoryId, mainIds, comparisonIds) => {
    updateFilters(categoryId, mainIds, comparisonIds);
  }, [updateFilters]);

  const handleTogglePage = useCallback(() => {
    setActivePage(prev => prev === 1 ? 2 : 1);
  }, []);

  // 필터 데이터 객체 구성
  const filterData = {
    categoryId: filters.categoryId,
    mainFilterGroupIds: filters.mainFilterGroupIds,
    comparisonFilterGroupIds: filters.comparisonFilterGroupIds
  };

  // Fetch both data sets at parent level
  const shouldFetch = !filters.isInitialMount;

  const marketTrendsState = useTrendState({
    filterData,
    tabKey: 'market_trends',
    shouldFetch
  });

  const productAnalysisState = useTrendState({
    filterData,
    tabKey: 'product_analysis',
    shouldFetch
  });

  // Page 2 API calls - only fetch when on page 2
  const shouldFetchPage2 = shouldFetch && activePage === 2;

  const consumerReactionState = useTrendState({
    filterData,
    tabKey: 'consumer_reaction',
    shouldFetch: shouldFetchPage2
  });

  const ingredientAnalysisState = useTrendState({
    filterData,
    tabKey: 'ingredient_analysis',
    shouldFetch: shouldFetchPage2
  });

  const sustainabilityState = useTrendState({
    filterData,
    tabKey: 'sustainability',
    shouldFetch: shouldFetchPage2
  });

  // Consolidated loading state
  const isLoadingPage1 = marketTrendsState.isLoading || productAnalysisState.isLoading;
  const isLoadingPage2 = consumerReactionState.isLoading || ingredientAnalysisState.isLoading || sustainabilityState.isLoading;
  const isLoading = activePage === 1 ? isLoadingPage1 : isLoadingPage2;

  const errorPage1 = marketTrendsState.error || productAnalysisState.error;
  const errorPage2 = consumerReactionState.error || ingredientAnalysisState.error || sustainabilityState.error;
  const error = activePage === 1 ? errorPage1 : errorPage2;

  const handleResetFilters = () => {
    window.location.reload();
  };

  const handleRetry = () => {
    if (activePage === 1) {
      marketTrendsState.retry();
      productAnalysisState.retry();
    } else {
      consumerReactionState.retry();
      ingredientAnalysisState.retry();
      sustainabilityState.retry();
    }
  };

  return (
    <div className="trends-page">
      {/* First-time user tutorial - starts when Analysis tab is visited */}
      <TrendTutorial shouldStart={hasVisitedAnalysis} />

      {/* Flex layout: Sidebar + Content */}
      <div className="trends-layout">
        {/* Left sidebar with filters */}
        <FilterPanel
          onFiltersApply={handleFiltersApply}
          initialCategoryId={INITIAL_CATEGORY_ID}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          brandState={brandState}
        />

        {/* Main content area */}
        <main className="trends-content">
          {/* Dashboard Tab Content */}
          {activeTab === 'dashboard' && <DashboardContent />}

          {/* Brand Tab Content */}
          {activeTab === 'brand' && (
            <BrandContentArea brandState={brandState} />
          )}

          {/* Analysis Tab Content */}
          {activeTab === 'analysis' && (
            <>
              {/* Tab Navigation Buttons */}
              {shouldFetch && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '8px',
                  marginBottom: '16px'
                }}>
                  {activePage === 2 && (
                    <button onClick={handleTogglePage} className="tab-nav-button">
                      <span className="tab-nav-indicator">&lt;&lt;</span>
                      <span>{translate('trendPage.navigation.previous')}</span>
                    </button>
                  )}
                  {activePage === 1 && (
                    <button onClick={handleTogglePage} className="tab-nav-button">
                      <span>{translate('trendPage.navigation.next')}</span>
                      <span className="tab-nav-indicator">&gt;&gt;</span>
                    </button>
                  )}
                </div>
              )}

              {/* Show guide when user first lands on the page */}
              {!shouldFetch && <EmptyStateGuide />}

              {/* Single consolidated loading/error state */}
              {isLoading && <LoadingSpinner />}
              {!isLoading && error && (
                <ErrorMessage
                  error={error}
                  onRetry={handleRetry}
                  onResetFilters={handleResetFilters}
                />
              )}
              {!isLoading && !error && shouldFetch && (
                <>
                  {/* Page 1 Content */}
                  {activePage === 1 && (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '16px',
                      width: '100%',
                      paddingBottom: 0,
                      marginBottom: 0
                    }}>
                      {/* Market Trends Section - Rows 1 & 2 */}
                      <section>
                        <MarketTrendsTab
                          filterData={filterData}
                          tabKey="market_trends"
                          shouldFetch={false}
                          preloadedData={marketTrendsState.data}
                        />
                      </section>

                      {/* Product Analysis Section - Row 3 onwards */}
                      <section style={{ marginBottom: 0 }}>
                        <ProductAnalysisTab
                          filterData={filterData}
                          tabKey="product_analysis"
                          shouldFetch={false}
                          preloadedData={productAnalysisState.data}
                        />
                      </section>
                    </div>
                  )}

                  {/* Page 2 Content - Consumer Reaction + Ingredient Analysis */}
                  {activePage === 2 && (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(6, 1fr)',
                      gap: '16px',
                      padding: '0',
                      width: '100%'
                    }}>
                  {/* Row 1: Summary + Sentiment Chart (3/6) and Aspect Chart (3/6) */}
                  {consumerReactionState.data && (
                    <>
                      <div style={{ gridColumn: 'span 3' }}>
                        <ChartContainer height="520px">
                          <Summary data={consumerReactionState.data?.data} />
                          <SentimentChart data={consumerReactionState.data?.data} />
                        </ChartContainer>
                      </div>

                      <div style={{ gridColumn: 'span 3' }}>
                        <ChartContainer height="520px">
                          <AspectChart data={consumerReactionState.data?.data} />
                        </ChartContainer>
                      </div>

                      {/* Row 2: Keywords Chart (4/6) and Ranking Chart (2/6) */}
                      <div style={{ gridColumn: 'span 4' }}>
                        <ChartContainer height="520px">
                          <KeywordsChart data={consumerReactionState.data?.data} />
                        </ChartContainer>
                      </div>

                      {ingredientAnalysisState.data && (
                        <div style={{ gridColumn: 'span 2' }}>
                          <ChartContainer height="520px">
                            <RankingChart data={ingredientAnalysisState.data?.data} />
                          </ChartContainer>
                        </div>
                      )}

                      {/* Row 3: Classification Analysis Chart (full width) */}
                      {ingredientAnalysisState.data && (
                        <div style={{ gridColumn: 'span 6' }}>
                          <ClassificationAnalysisChart data={ingredientAnalysisState.data?.data} />
                        </div>
                      )}

                      {/* Row 4: Category Analysis Chart (full width) */}
                      {ingredientAnalysisState.data && (
                        <div style={{ gridColumn: 'span 6' }}>
                          <CategoryAnalysisChart data={ingredientAnalysisState.data?.data} />
                        </div>
                      )}

                      {/* Row 5: Occurrence Chart (full width) */}
                      {ingredientAnalysisState.data && (
                        <div style={{ gridColumn: 'span 6' }}>
                          <ChartContainer autoHeight={true}>
                            <OccurrenceChart data={ingredientAnalysisState.data?.data} />
                          </ChartContainer>
                        </div>
                      )}

                      {/* Row 6: Sustainability Status Chart + Certification Chart */}
                      {sustainabilityState.data && (
                        <>
                          <div style={{ gridColumn: 'span 3', display: 'flex' }}>
                            <ChartContainer autoHeight={true} style={{ flex: 1 }}>
                              <SustainabilityStatusChart data={sustainabilityState.data?.data} />
                            </ChartContainer>
                          </div>
                          <div style={{ gridColumn: 'span 3', display: 'flex' }}>
                            <ChartContainer autoHeight={true} style={{ flex: 1 }}>
                              <CertificationChart data={sustainabilityState.data?.data} />
                            </ChartContainer>
                          </div>
                        </>
                      )}
                    </>
                  )}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
};

/**
 * 트렌드 페이지의 최상위 컴포넌트
 * TrendsProvider로 컨텍스트를 제공하고 TrendsPageContent를 렌더링
 */
const TrendsPage = () => (
  <TrendsProvider>
    <TrendsPageContent />
  </TrendsProvider>
);

export default TrendsPage;